import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { BigQueryAuthService } from 'src/app/components/logging/big-query-auth.service';

const BIGQUERY_BASE_URL =
  'https://www.googleapis.com/bigquery/v2/projects/client-side-events/datasets/AI_Events/tables';

@Injectable({
  providedIn: 'root',
})
export class AIChatbotBaseLoggingService {
  private readonly bigQueryAuth = inject(BigQueryAuthService);

  constructor(
    protected http: HttpClient,
    protected userStateService: UserStateService,
    protected companyStateService: CompanyStateService,
  ) {}

  protected getToken(): Observable<string> {
    return from(this.bigQueryAuth.getToken()).pipe(
      catchError((error) => {
        console.error('Error getting token for BigQuery:', error);
        return of(null as any);
      }),
    );
  }

  protected insertData(tableUrl: string, jsonData: any): Observable<any> {
    return this.getToken().pipe(
      switchMap((token) => {
        if (!token) {
          return of(null);
        }

        const insertData = {
          kind: 'bigquery#tableDataInsertAllRequest',
          skipInvalidRows: false,
          ignoreUnknownValues: false,
          rows: [
            {
              insertId: Math.random().toString(36).substring(2).toUpperCase(),
              json: jsonData,
            },
          ],
        };

        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        return this.http.post(tableUrl, insertData, { headers }).pipe(
          switchMap((response: any) => {
            if (response.insertErrors && response.insertErrors.length > 0) {
              console.error(
                'Error posting to BigQuery:',
                response.insertErrors,
              );
              return of(null);
            }
            return of(response);
          }),
          catchError((error) => {
            console.error('Error posting to BigQuery:', error);
            return of(null);
          }),
        );
      }),
      catchError((error) => {
        console.error('Error getting token for BigQuery:', error);
        return of(null);
      }),
    );
  }

  protected getTableUrl(tableName: string): string {
    return `${BIGQUERY_BASE_URL}/${tableName}/insertAll`;
  }

  protected buildCommonFields(): {
    ts: string;
    host: string;
    company_id: string;
    username: string;
  } {
    return {
      ts: new Date().toISOString(),
      host: window.location.hostname,
      company_id: this.companyStateService.getSelectedCompanyId() || '',
      username: this.userStateService.getUsername() || '',
    };
  }
}
